import React from 'react';

/**
 * Custom Rotativa Gaúcha logo (SVG).
 * Inspired by the Instagram brand image: stacked "GAÚCHA / ROTATIVA"
 * with a flame mark. Used in Header, Footer, Hero, and Admin.
 *
 * Variants:
 *   - "horizontal" (default): icon + stacked text on the right (for headers)
 *   - "stacked": icon on top, text below (for hero / centered displays)
 *   - "wordmark-only": just text (rare)
 *
 * Sizes: "sm", "md", "lg", "xl", "hero"
 */

const SIZES = {
  sm: { icon: 28, big: 16, small: 11, gap: 8, tracking: 0.16, trackingSmall: 0.30 },
  md: { icon: 36, big: 22, small: 14, gap: 10, tracking: 0.16, trackingSmall: 0.30 },
  lg: { icon: 56, big: 36, small: 22, gap: 14, tracking: 0.16, trackingSmall: 0.30 },
  xl: { icon: 80, big: 56, small: 32, gap: 18, tracking: 0.16, trackingSmall: 0.30 },
  // Hero size is auto-responsive: smaller on phones to prevent letter-spacing overflow.
  hero: {
    icon: 'clamp(72px, 14vw, 110px)',
    big: 'clamp(40px, 9vw, 84px)',
    small: 'clamp(20px, 5vw, 46px)',
    gap: 22,
    tracking: 0.16,
    trackingSmall: 0.24, // slightly tighter than other sizes to fit "ROTATIVA"
  },
};

const FlameMark = ({ size = 36 }) => {
  const dim = typeof size === 'number' ? `${size}px` : size;
  return (
  <svg
    width={dim}
    height={dim}
    viewBox="0 0 64 64"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <defs>
      <linearGradient id="rg-flame" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#dc2626" />
        <stop offset="55%" stopColor="#ea580c" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
      <linearGradient id="rg-flame-inner" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#fef3c7" />
      </linearGradient>
    </defs>
    {/* Outer flame */}
    <path
      d="M32 4
         C 30 14, 22 18, 22 28
         C 22 33, 25 36, 28 36
         C 27 31, 28 26, 32 22
         C 33 28, 38 31, 42 28
         C 44 33, 46 36, 46 42
         C 46 52, 38 60, 32 60
         C 24 60, 16 52, 16 42
         C 16 32, 24 24, 26 18
         C 28 14, 30 10, 32 4 Z"
      fill="url(#rg-flame)"
    />
    {/* Inner flame */}
    <path
      d="M32 28
         C 30 32, 27 36, 27 42
         C 27 48, 30 52, 32 52
         C 35 52, 38 48, 38 42
         C 38 36, 34 32, 32 28 Z"
      fill="url(#rg-flame-inner)"
    />
  </svg>
  );
};

export const Logo = ({
  variant = 'horizontal',
  size = 'md',
  onClick,
  className = '',
  showTagline = false,
}) => {
  const s = SIZES[size] || SIZES.md;

  if (variant === 'stacked') {
    return (
      <div
        data-testid="brand-logo-stacked"
        onClick={onClick}
        className={`flex flex-col items-center select-none ${onClick ? 'cursor-pointer' : ''} ${className}`}
      >
        <FlameMark size={s.icon} />
        <div className="text-center mt-2 leading-none">
          <div
            className="font-black tracking-[0.18em] text-white"
            style={{ fontSize: s.big, lineHeight: 1 }}
          >
            GAÚCHA
          </div>
          <div
            className="font-bold tracking-[0.32em] text-amber-500 mt-1"
            style={{ fontSize: s.small, lineHeight: 1 }}
          >
            ROTATIVA
          </div>
          {showTagline && (
            <div className="text-stone-400 text-xs italic mt-2">
              Costelão raiz, feito no RS
            </div>
          )}
        </div>
      </div>
    );
  }

  // horizontal (default)
  return (
    <div
      data-testid="brand-logo"
      onClick={onClick}
      className={`flex items-center select-none ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{ gap: s.gap }}
    >
      <FlameMark size={s.icon} />
      <div className="leading-none">
        <div
          className="font-black tracking-[0.16em] text-white"
          style={{ fontSize: s.big, lineHeight: 1 }}
        >
          GAÚCHA
        </div>
        <div
          className="font-bold tracking-[0.30em] text-amber-500 mt-1"
          style={{ fontSize: s.small, lineHeight: 1 }}
        >
          ROTATIVA
        </div>
      </div>
    </div>
  );
};

export default Logo;
